import { useEffect, useRef, useState } from 'react';

interface Detector {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}
declare global {
  interface Window {
    BarcodeDetector?: new (opts: { formats: string[] }) => Detector;
  }
}

const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf', 'qr_code'];

/**
 * Continuous camera scanning. Uses the browser's native BarcodeDetector when present
 * (Chrome on Android), otherwise ZXing loaded on demand. Calls onCode once per read.
 */
export function CameraScanner({ onCode }: { onCode: (code: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [status, setStatus] = useState('กำลังเปิดกล้อง…');
  const [error, setError] = useState('');
  const done = useRef(false);

  useEffect(() => {
    let stop = () => {};
    let cancelled = false;
    const emit = (code: string) => {
      if (done.current || !code) return;
      done.current = true;
      onCode(code);
    };

    (async () => {
      try {
        if (window.BarcodeDetector) {
          const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
          if (cancelled) return stream.getTracks().forEach((t) => t.stop());
          const v = video.current!;
          v.srcObject = stream;
          await v.play();
          const det = new window.BarcodeDetector({ formats: FORMATS });
          let alive = true;
          stop = () => {
            alive = false;
            stream.getTracks().forEach((t) => t.stop());
          };
          setStatus('หันกล้องไปที่ Barcode');
          const tick = async () => {
            if (!alive) return;
            try {
              const r = await det.detect(v);
              if (r[0]?.rawValue) return emit(r[0].rawValue);
            } catch {
              /* frame not ready */
            }
            setTimeout(tick, 120);
          };
          void tick();
        } else {
          const { BrowserMultiFormatReader } = await import('@zxing/browser');
          if (cancelled) return;
          const reader = new BrowserMultiFormatReader();
          const controls = await reader.decodeFromConstraints({ video: { facingMode: 'environment' } }, video.current!, (result) => {
            if (result) emit(result.getText());
          });
          stop = () => controls.stop();
          setStatus('หันกล้องไปที่ Barcode');
        }
      } catch (e) {
        const name = (e as { name?: string })?.name;
        setError(
          name === 'NotAllowedError'
            ? 'ไม่ได้รับอนุญาตให้ใช้กล้อง — เปิดสิทธิ์กล้องใน Browser แล้วลองใหม่'
            : name === 'NotFoundError'
              ? 'ไม่พบกล้องในเครื่องนี้'
              : `เปิดกล้องไม่ได้: ${(e as Error)?.message ?? e}`,
        );
      }
    })();

    return () => {
      cancelled = true;
      stop();
    };
  }, [onCode]);

  return (
    <div>
      <video ref={video} playsInline muted className="max-h-[60vh] w-full rounded-lg bg-black" />
      <p className={`mt-2 text-sm ${error ? 'text-bad' : 'text-muted'}`}>{error || status}</p>
    </div>
  );
}
